import { useMemo, useState } from "react"
import { useLocation, useRouteLoaderData } from "react-router"
import { DocsRouterLink as RouterLink } from "~/docs-data/docs-link"
import { ComingSoonDialog } from "~/components/ComingSoonGuide"
import {
  LockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MapPinIcon,
  VectorSquareIcon,
  SplitIcon,
} from "lucide-react"
import { H1, P } from "~/components/Typography"
import {
  guides,
  isLive,
  THEMES,
  getTheme,
  type Example,
  type GuideEntry,
  type Theme,
} from "~/docs-data/guides"
import { getGuideBody } from "~/docs-data/guide-bodies"
import { getExampleBody } from "~/docs-data/example-bodies"
import { cn } from "~/lib/utils"

const isCaseStudy = (g: GuideEntry) => g.tags.includes("case-study")
const isGetStarted = (g: GuideEntry) => g.tags.includes("get-started")
// Platform guides: capability walkthroughs (exports, custom locations) —
// audience-neutral and un-themed, so they get their own section.
const isPlatform = (g: GuideEntry) => g.tags.includes("platform")
const isHowTo = (g: GuideEntry) =>
  !isCaseStudy(g) && !isGetStarted(g) && !isPlatform(g)
// "Unfinished" = no authored markdown body. These get gated behind the same
// request-access paywall as customer stories, so we also surface the lock
// icon in the card listing.
const isUnfinished = (g: GuideEntry) => !isCaseStudy(g) && !getGuideBody(g.slug)

// Authored, country-specific example recipes across all published guides —
// surfaced as their own searchable section on this index.
const AUTHORED_EXAMPLES: { guide: GuideEntry; ex: Example }[] = guides
  .filter(isLive)
  .flatMap((g) =>
    (g.examples ?? [])
      .filter((ex) => getExampleBody(g.slug, ex.slug))
      .map((ex) => ({ guide: g, ex })),
  )

const USED_THEME_IDS = new Set(guides.flatMap((g) => g.themes ?? []))
const ALL_THEMES = THEMES.filter((t) => USED_THEME_IDS.has(t.id))

const FEATURED_HOWTO_SLUGS = new Set([
  "brand-comparison",
  "store-profiling",
  "area-identification",
])

const VISIBLE_THRESHOLD = 3

export default function GuidesIndex() {
  // Search and secondary filters are parked until the catalog is big enough
  // to need them — the theme chips are the only browse control for now.
  const [theme, setTheme] = useState<string | null>(null)

  const anyFilter = !!theme

  const matches = (g: GuideEntry) =>
    !theme || (g.themes ?? []).includes(theme as Theme)

  const sortHowTo = (a: GuideEntry, b: GuideEntry) => {
    const af = FEATURED_HOWTO_SLUGS.has(a.slug) ? 0 : 1
    const bf = FEATURED_HOWTO_SLUGS.has(b.slug) ? 0 : 1
    return af - bf
  }

  const howTo = useMemo(
    () => guides.filter(isHowTo).filter(isLive).filter(matches).sort(sortHowTo),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme],
  )
  // Get-started guides are universal onboarding — industry / location-type
  // filters don't apply to them (they'd only make the section vanish).
  // Text search still narrows them.
  // One section for onboarding + capability walkthroughs — both are
  // un-themed, so the theme filter doesn't apply to them.
  const gettingStarted = [
    ...guides.filter(isGetStarted),
    ...guides.filter(isPlatform).filter(isLive),
  ]
  const exampleItems = useMemo(
    () =>
      AUTHORED_EXAMPLES.filter(
        ({ guide: g }) => !theme || (g.themes ?? []).includes(theme as Theme),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme],
  )

  // Drafts stay out; while all customer stories are drafts the whole
  // section disappears.
  const stories = useMemo(
    () => guides.filter(isCaseStudy).filter(isLive).filter(matches),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme],
  )

  return (
    <div className="max-w-screen-xl">
      <H1 className="!mb-1 mt-1">Guides &amp; examples</H1>
      <P className="text-sm text-muted-foreground mt-1 mb-5">
        How-to guides and customer stories. Pick a goal or browse by theme.
      </P>

      <Chips
        value={theme}
        onChange={setTheme}
        options={ALL_THEMES.map((t) => ({ value: t.id, label: t.label, title: t.description }))}
      />

      {/* Section order mirrors the top-bar mega menu: Get started first,
          then How-to guides, then Customer stories. */}

      <GuidesSection
        title="How-to guides"
        subtitle="What can be done with the data: concept-level guides to each analysis, independent of tools."
        items={howTo}
        gridClass="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
        emptyText="No how-to guides match this filter."
        anyFilter={anyFilter}
        renderCard={(g) => <GuideCard key={g.slug} guide={g} />}
      />

      {exampleItems.length > 0 && (
        <section id="examples" className="mt-10 scroll-mt-6">
          <div className="flex items-baseline gap-2 mb-1">
            <h2 className="text-lg font-semibold text-foreground">Examples</h2>
            <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              {exampleItems.length}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Worked, runnable recipes, country-specific and run end-to-end on real data.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {exampleItems.map(({ guide: g, ex }) => (
              <RouterLink
                key={`${g.slug}/${ex.slug}`}
                to={`/docs/guides/${g.slug}/${ex.slug}`}
                className="group flex flex-col rounded-lg border bg-card hover:border-primary hover:shadow-sm transition-all p-5"
              >
                <div className="flex items-center gap-1.5 mb-2 text-[10px] font-medium">
                  {ex.country && <span className="border rounded px-1.5 py-0.5">{ex.country}</span>}
                  <span className="text-muted-foreground ml-auto">{g.title}</span>
                </div>
                <h3 className="font-semibold text-sm group-hover:text-primary transition-colors mb-2">
                  {ex.title}
                </h3>
                <p className="text-xs text-muted-foreground line-clamp-3">{ex.description}</p>
              </RouterLink>
            ))}
          </div>
        </section>
      )}

      <GuidesSection
        title="Using the platform"
        subtitle="First steps and capability walkthroughs, from your first query to bulk exports and custom locations."
        items={gettingStarted}
        gridClass="grid-cols-1 md:grid-cols-2"
        emptyText="No guides match this filter."
        anyFilter={anyFilter}
        renderCard={(g) => <GuideCard key={g.slug} guide={g} />}
      />

      {stories.length > 0 && (
        <GuidesSection
          id="customer-stories"
          title="Customer stories"
          subtitle="How real organisations use Pine59 movement data in production."
          items={stories}
          gridClass="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          emptyText="No customer stories match this filter."
          anyFilter={anyFilter}
          renderCard={(g) => <GuideCard key={g.slug} guide={g} />}
        />
      )}
    </div>
  )
}

function GuidesSection({
  id,
  title,
  subtitle,
  items,
  gridClass,
  emptyText,
  anyFilter,
  renderCard,
}: {
  id?: string
  title: string
  subtitle: string
  items: GuideEntry[]
  gridClass: string
  emptyText: string
  anyFilter: boolean
  renderCard: (g: GuideEntry) => React.ReactNode
}) {
  const [expanded, setExpanded] = useState(false)
  const showAll = expanded || anyFilter
  const visible = showAll ? items : items.slice(0, VISIBLE_THRESHOLD)
  const hiddenCount = Math.max(0, items.length - VISIBLE_THRESHOLD)

  return (
    <section id={id} className="mt-10 scroll-mt-6">
      <div className="flex items-baseline gap-2 mb-1">
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
          {items.length}
        </span>
      </div>
      <p className="text-sm text-muted-foreground mb-4">{subtitle}</p>

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground border rounded-md py-6 text-center">{emptyText}</p>
      ) : (
        <>
          <div className={cn("grid gap-4", gridClass)}>{visible.map(renderCard)}</div>
          {hiddenCount > 0 && !anyFilter && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-primary transition-colors"
            >
              {expanded ? (
                <>
                  Show less
                  <ChevronUpIcon className="w-3.5 h-3.5" />
                </>
              ) : (
                <>
                  Show {hiddenCount} more
                  <ChevronDownIcon className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          )}
        </>
      )}
    </section>
  )
}

// Cards were drowning in tags (themes + location types all as
// pills) — cap the row and fold the rest into a "+N" counter. One tag row
// shared by every card type so the metadata selection is consistent.
const MAX_VISIBLE_TAGS = 3

function GuideTagRow({ guide }: { guide: GuideEntry }) {
  const pills: React.ReactNode[] = [
    ...(guide.themes ?? []).map((id) => {
      const meta = getTheme(id)
      return (
        <YellowTag key={id} title={meta?.description}>
          {meta?.label ?? id}
        </YellowTag>
      )
    }),
  ]
  const visible = pills.slice(0, MAX_VISIBLE_TAGS)
  const overflow = pills.length - visible.length

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible}
      {overflow > 0 && (
        <span className="text-[10px] text-muted-foreground px-1">+{overflow}</span>
      )}
    </div>
  )
}

function GuideCard({ guide }: { guide: GuideEntry }) {
  const unfinished = isUnfinished(guide)
  const [comingSoonOpen, setComingSoonOpen] = useState(false)
  const consoleData = useRouteLoaderData("routes/_console") as
    | { userEmail?: string }
    | undefined
  const location = useLocation()

  const cardBody = (
    <>
      <h3 className="font-semibold text-sm group-hover:text-primary transition-colors mb-2 flex items-center gap-1.5">
        {(guide.locked || unfinished) && (
          <LockIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        )}
        <span className="flex-1">{guide.title}</span>
      </h3>
      <p className="text-xs text-muted-foreground line-clamp-3 mb-3 flex-1">{guide.description}</p>
      <GuideTagRow guide={guide} />
    </>
  )

  const cardClass =
    "group flex flex-col rounded-lg border bg-card hover:border-primary hover:shadow-sm transition-all p-5"

  // Coming-soon guides open the interest dialog in place — no navigation to
  // a blurred detail page you can't easily back out of.
  if (unfinished) {
    return (
      <>
        <button
          type="button"
          onClick={() => setComingSoonOpen(true)}
          className={cn(cardClass, "text-left")}
        >
          {cardBody}
        </button>
        <ComingSoonDialog
          slug={guide.slug}
          userEmail={consoleData?.userEmail}
          loginHref={`/login?redirectTo=${encodeURIComponent(location.pathname)}`}
          open={comingSoonOpen}
          onOpenChange={setComingSoonOpen}
        />
      </>
    )
  }

  return (
    <RouterLink to={`/docs/guides/${guide.slug}`} className={cardClass}>
      {cardBody}
    </RouterLink>
  )
}


function YellowTag({
  children,
  title,
}: {
  children: React.ReactNode
  title?: string
}) {
  return (
    <span
      title={title}
      className="text-[10px] uppercase tracking-wide font-medium bg-yellow-100 dark:bg-yellow-500/15 text-yellow-800 dark:text-yellow-300 rounded px-1.5 py-0.5"
    >
      {children}
    </span>
  )
}

const LOC_ICON = {
  place: MapPinIcon,
  area: VectorSquareIcon,
  way: SplitIcon,
} as const


type ChipOption = { value: string; label: string; title?: string }

function Chips({
  value,
  onChange,
  options,
}: {
  value: string | null
  onChange: (v: string | null) => void
  options: ChipOption[]
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <ChipButton active={value === null} onClick={() => onChange(null)}>
        All
      </ChipButton>
      {options.map((o) => (
        <ChipButton
          key={o.value}
          active={value === o.value}
          onClick={() => onChange(o.value)}
          title={o.title}
        >
          {o.label}
        </ChipButton>
      ))}
    </div>
  )
}

function ChipsRow({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string | null
  onChange: (v: string | null) => void
  options: ChipOption[]
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs uppercase tracking-wide text-muted-foreground w-28 shrink-0">{label}</span>
      <Chips value={value} onChange={onChange} options={options} />
    </div>
  )
}

function ChipButton({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean
  onClick: () => void
  title?: string
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={cn(
        "text-xs px-2.5 py-1 rounded-full border transition-colors",
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-background hover:bg-accent",
      )}
    >
      {children}
    </button>
  )
}
